import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import classnames from 'classnames';
import { t } from 'services/translation';
import './index.scss';
import { WarningIcon as WarningIcn } from 'views/ui/icons';
import { isActionKey } from 'ui_system/utilities/KeyboardHelpers';
import { Theme } from 'ui_system/theme';
import TitlebarActions from 'actions/titlebar';
import ModalActions from 'actions/modal';
import AlterBookingModal from 'views/anyguide/bookings/alter';
import { ReactModal } from 'ui_system/modals/ReactModal';
import MultiSelectOption from './option/MultiSelectOption';
import { MultiSelectGuestOption } from './GuestOfGuest/MultiSelectGuestOption';
import { MultiSelectGuestDesktop } from './GuestOfGuest/MultiSelectGuest';
import { buildCollapsedLabel, buildEditBookingLabel } from './guests/Helpers';

const WarningIcon = styled(WarningIcn)`
  fill: #d82f2f;
`;

const { number, array, object, func } = PropTypes;

const GuestListModal = styled(
  ReactModal(MultiSelectGuestOption, {
    isBackgroundClickable: false,
    isCloseButtonHidden: true,
  })
)`
  padding: 0;
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    width: 700px;
  }
`;

class MultiSelectDropdown extends React.Component {
  static propTypes = {
    min: number.isRequired,
    max: number.isRequired,
    rootLabels: object.isRequired,
    options: array.isRequired,
    onChange: func,
    removeGuest: PropTypes.func,
    guestList: PropTypes.array,
    inPlaceEdit: PropTypes.bool,
    for_guest: PropTypes.bool,
    disabled: PropTypes.bool,
    bookingId: number.isRequired,
    country: PropTypes.string,
    resetGuestList: PropTypes.func,
    initialTicketQuantity: number.isRequired,
    capacityError: PropTypes.string,
    slotTicketCapacities: PropTypes.array,
    hasTicketCapacityEnabled: PropTypes.bool,
    timeSelected: PropTypes.bool,
    tourTicketCapacities: PropTypes.array,
  };

  static defaultProps = {
    inPlaceEdit: false,
    for_guest: false,
    disabled: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      expanded: false,
      showGuestListModal: false,
      expandedId: '',
    };
  }

  componentDidUpdate(prevProps) {
    const { capacityError } = this.props;
    // Only expand if there's a new, unique capacity error (different timestamp)
    if (
      capacityError !== prevProps.capacityError &&
      capacityError &&
      !this.state.expanded
    ) {
      // Expand the dropdown if there is a new capacity error and it's not already
      this.setState({ expanded: true });
    }
  }

  onDecrease = (id) => {
    if (this.isDecreasable()) {
      this.props.onChange(id, -1);
    }
  };

  onIncrease = (id) => {
    this.props.onChange(id, +1);
  };

  onChange = (id, newValue, totalChange) => {
    const { totalCount, min, max } = this.props;
    // prevent total value from being out of range of the min/max
    const newTotal = totalCount - totalChange;
    if (newTotal >= min && newTotal <= max) {
      this.props.onChange(id, null, newValue);
    } else if (newTotal < min) {
      this.props.onChange(id, null, 1);
    }
  };

  isDecreasable() {
    const { totalCount } = this.props;
    return totalCount > 0;
  }

  expand = () => {
    const { isGuestOfGuestEnabled, disabled } = this.props;
    if (disabled) return;
    this.setState({ expanded: true });
    const { isMobile } = Theme.mobile;
    const shouldShowGogEditBooking = isGuestOfGuestEnabled;
    if (isMobile && shouldShowGogEditBooking) {
      this.setState({
        editBookingStep: 2,
        showGuestListModal: true,
      });
    }
  };

  multiSelectDone = () => {
    const { totalCount, min, max } = this.props;
    const disabled = totalCount < min || totalCount > max;

    if (!disabled) {
      this.setState({ expanded: false });
    }
  };

  onCloseGuestModal = () => {
    this.props.resetGuestList();
    this.setState({ showGuestListModal: false, expanded: false });
  };

  _renderDropdown() {
    const {
      for_guest,
      isGuestOfGuestEnabled,
      unitTypes,
      value,
      guestList,
      initialTicketQuantity,
    } = this.props;
    const multiSelectDropdownClasses = classnames(
      'ui',
      'multi-select-dropdown',
      {
        disabled: this.props.disabled,
      }
    );

    return (
      <button
        type="button"
        data-testid="multi-select-dropdown"
        ref="multiSelectDropdown"
        className={multiSelectDropdownClasses}
        onClick={this.expand}
        onKeyDown={(e) => isActionKey(e.key) && this.expand()}
      >
        <div className="text">
          {!for_guest && isGuestOfGuestEnabled
            ? buildEditBookingLabel(unitTypes, guestList)
            : buildCollapsedLabel(unitTypes, value, initialTicketQuantity)}
        </div>
        <i className="af-triangle-down-2" />
      </button>
    );
  }

  _renderMultiSelectOptions() {
    const {
      options,
      slotTicketCapacities,
      tourTicketCapacities,
      for_guest: forGuest,
      renderToolTip,
      inPlaceEdit,
      hasTicketCapacityEnabled,
    } = this.props;
    return options?.map((option, index) => {
      if (option.internalOnly && forGuest) {
        return null;
      }

      const slotTicketCapacity = slotTicketCapacities?.find(
        (capacity) => capacity.unit_type_id === option.id
      );
      const tourTicketCapacity = tourTicketCapacities?.find(
        (capacity) => capacity.unit_type_id === option.id
      );

      return (
        <MultiSelectOption
          key={option.id}
          id={option.id}
          name={option.noun_plural}
          description={option.description}
          price={option.price}
          quantity={option.count}
          renderToolTip={renderToolTip}
          onDecrease={this.onDecrease}
          onIncrease={this.onIncrease}
          onChange={this.onChange}
          inPlaceEdit={inPlaceEdit}
          decreasable={this.isDecreasable()}
          increasable={true}
          onExpandIdChange={(id) => this.setState({ expandedId: id })}
          expandedId={this.state.expandedId}
          hasTicketCapacityEnabled={hasTicketCapacityEnabled}
          slotTicketCapacity={slotTicketCapacity}
          tourTicketCapacity={tourTicketCapacity}
        />
      );
    });
  }

  _checkCapacityExceeded(capacities) {
    const { value, options, tourTicketCapacities } = this.props;
    return value.some((selectedValue) => {
      const selectedOption = options.find(
        (option) => option.id === selectedValue.id
      );
      const matchingCapacity = capacities?.find(
        (capacity) => capacity.unit_type_id === selectedOption?.id
      );
      return (
        matchingCapacity &&
        selectedOption?.count >
          (capacities === tourTicketCapacities
            ? matchingCapacity.capacity
            : matchingCapacity.availability)
      );
    });
  }

  _renderCapacityWarning() {
    return (
      <div
        ref="multiSelectOptionsWarning"
        className="multi-select-warning"
        data-testid="multi-select-warning"
      >
        <WarningIcon />
        <div data-testid="ticket-capacity-warning">
          {t('Exceeds limit. Please adjust your selection.')}
        </div>
      </div>
    );
  }

  _renderWarning() {
    let warning = null;
    let noun = null;
    let minOrMax = null;
    const {
      min,
      max,
      rootLabels,
      capacityError,
      totalCount,
      slotTicketCapacities,
      options,
      hasTicketCapacityEnabled,
      value,
      timeSelected,
      tourTicketCapacities,
    } = this.props;
    const totalQuantity = totalCount;

    if (totalQuantity > max) {
      noun = max === 1 ? rootLabels.singular : rootLabels.plural;
      minOrMax = 'maximum';
    } else if (totalQuantity < min) {
      noun = min === 1 ? rootLabels.singular : rootLabels.plural;
      minOrMax = 'minimum';
    }

    const isMaximum = minOrMax === 'maximum';

    if (hasTicketCapacityEnabled) {
      if (!timeSelected && slotTicketCapacities.length === 0) {
        if (this._checkCapacityExceeded(tourTicketCapacities)) {
          warning = this._renderCapacityWarning();
        }
      }

      if (this._checkCapacityExceeded(slotTicketCapacities)) {
        warning = this._renderCapacityWarning();
      }
    }

    if (minOrMax && noun) {
      warning = (
        <div
          ref="multiSelectOptionsWarning"
          className="multi-select-warning"
          data-testid="multi-select-warning"
        >
          <WarningIcon />
          <div>
            {' '}
            {isMaximum
              ? ' ' +
                `${t(`A_MAXIMUM_OF_GUESTS_ALLOWED_PER_BOOKING`, '', {
                  count: max,
                  noun,
                })}`
              : ' ' +
                `${t(`A_MINIMUM_OF_GUESTS_IS_REQUIRED_TO_BOOK`, '', {
                  count: min,
                  noun,
                })}`}
          </div>
        </div>
      );
    }

    if (capacityError?.length > 0) {
      warning = (
        <div
          ref="multiSelectOptionsWarning"
          className="multi-select-warning"
          data-testid="multi-select-warning"
        >
          <WarningIcon />
          <div data-testId="ticket-capacity-warning">
            {t(
              'THE_TOTAL_NUMBER_OF_GUESTS_SELECTED_EXCEEDS_THE_MAXIMUM_CAPACITY'
            )}
          </div>
        </div>
      );
    }

    return warning;
  }

  _renderMultiSelect() {
    const { totalCount, min, max } = this.props;
    const disabled = totalCount < min || totalCount > max;
    return (
      <div ref="multiSelectOptionsContainer" tabIndex={0} role="listbox">
        <div
          data-testid="multi-selector"
          ref="multiSelectOptions"
          className="ui multi-select-options"
        >
          {this._renderMultiSelectOptions()}
        </div>
        {this._renderWarning()}
        <div
          ref="multiSelectDone"
          className="multi-select-done-wrapper"
          data-testid="done-btn"
        >
          <button
            type="button"
            aria-label="Ticket selection complete"
            className={`ui button Button multi-select-done brand-bg ${
              disabled && 'disabled'
            }`}
            onClick={this.multiSelectDone}
            onKeyDown={(e) => isActionKey(e.key) && this.multiSelectDone()}
            data-testid="done-button"
          >
            {t('DONE')}
          </button>
        </div>
      </div>
    );
  }

  _renderMultiSelectGuest() {
    const {
      guestList,
      removeGuest,
      units,
      addGuest,
      updateGuest,
      country,
      onUpdateGuestDetail,
    } = this.props;
    const { isMobile } = Theme.mobile;
    if (!isMobile) {
      return (
        <MultiSelectGuestDesktop
          guestList={guestList}
          isMobile={isMobile}
          multiSelectDone={this.multiSelectDone}
          removeGuest={removeGuest}
          units={units.items}
          addGuest={addGuest}
          updateGuest={updateGuest}
          onUpdateGuestDetail={onUpdateGuestDetail}
          country={country}
        />
      );
    }
    return '';
  }

  render() {
    const {
      isGuestOfGuestEnabled,
      guestList,
      removeGuest,
      units,
      addGuest,
      updateGuest,
      country,
    } = this.props;
    const { expanded, showGuestListModal } = this.state;
    const shouldShowGogEditBooking = isGuestOfGuestEnabled;
    const renderMultiSelectComponent = shouldShowGogEditBooking
      ? this._renderMultiSelectGuest()
      : this._renderMultiSelect();
    return (
      <div
        ref="multiSelectContainer"
        className="multi-select-wrapper"
        data-testid="multiselect-dropdown"
      >
        {!expanded ? this._renderDropdown() : renderMultiSelectComponent}
        {showGuestListModal && (
          <GuestListModal
            isOpen={showGuestListModal}
            guestList={guestList}
            isMobile={Theme.mobile.isMobile}
            removeGuest={removeGuest}
            onClose={this.onCloseGuestModal}
            onDone={() => {
              this.setState({ showGuestListModal: false, expanded: false });
            }}
            units={units.items}
            addGuest={addGuest}
            updateGuest={updateGuest}
            country={country}
          />
        )}
      </div>
    );
  }
}

export default MultiSelectDropdown;
